const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const urlRegex =
  /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/[\w-./?%&=]*)?$/i;
const uuidRegex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isEmail(value: string) {
  return emailRegex.test(value);
}

export function isUrl(value: string) {
  return urlRegex.test(value);
}

export function isUUID(value: string) {
  return uuidRegex.test(value);
}

export function isEmpty(value: unknown) {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value as object).length === 0;
  return false;
}

export function minLength(value: string, length: number) {
  return value.trim().length >= length;
}

export function maxLength(value: string, length: number) {
  return value.trim().length <= length;
}

export function containsEmail(value: string) {
  return /\b[^\s@]+@[^\s@]+\.[^\s@]+\b/.test(value);
}

export function containsUrl(value: string) {
  return /(https?:\/\/|www\.)[\w.-]+\.[a-z]{2,}/i.test(value);
}

export function containsNumber(value: string) {
  return /\d/.test(value);
}

export function containsWord(value: string, word: string, { caseSensitive = false } = {}) {
  if (!word) return false;
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const flags = caseSensitive ? 'g' : 'gi';
  const regex = new RegExp(`\\b${escaped}\\b`, flags);
  return regex.test(value);
}

export function containsAny(value: string, terms: string[]) {
  return terms.some((term) => value.toLowerCase().includes(term.toLowerCase()));
}

/**
 * Form validators that return error messages
 * Used for consistent form validation across authentication pages
 */
export const validators = {
  /**
   * Validates email format
   */
  email: (value: string): true | string => {
    return isEmail(value) || 'Invalid email address';
  },

  /**
   * Validates password strength
   * Requirements: min 8 chars, 1 uppercase, 1 number, 1 special char
   */
  password: (value: string): true | string => {
    if (value.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(value)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[0-9]/.test(value)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)) {
      return 'Password must contain at least one special character';
    }
    return true;
  },

  /**
   * Validates that field is not empty
   */
  required: (value: string): true | string => {
    return !isEmpty(value) || 'This field is required';
  },

  /**
   * Validates that two passwords match
   */
  matchPassword: (value: string, password: string): true | string => {
    return value === password || 'Passwords do not match';
  },

  /**
   * Validates minimum length with custom message
   */
  minLength: (min: number) => (value: string): true | string => {
    return minLength(value, min) || `Must be at least ${min} characters`;
  },

  /**
   * Validates maximum length with custom message
   */
  maxLength: (max: number) => (value: string): true | string => {
    return maxLength(value, max) || `Must be at most ${max} characters`;
  },

  /**
   * Calculates password strength (0-100)
   */
  passwordStrength: (password: string): number => {
    let strength = 0;

    // Length contribution (max 40 points)
    if (password.length >= 8) strength += 20;
    if (password.length >= 12) strength += 10;
    if (password.length >= 16) strength += 10;

    // Character variety (max 60 points)
    if (/[a-z]/.test(password)) strength += 10;
    if (/[A-Z]/.test(password)) strength += 15;
    if (/[0-9]/.test(password)) strength += 15;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) strength += 20;

    return Math.min(strength, 100);
  },

  /**
   * Gets password strength label
   */
  passwordStrengthLabel: (strength: number): string => {
    if (strength < 30) return 'Weak';
    if (strength < 60) return 'Fair';
    if (strength < 80) return 'Good';
    return 'Strong';
  },

  /**
   * Gets password strength color
   */
  passwordStrengthColor: (strength: number): string => {
    if (strength < 30) return 'red';
    if (strength < 60) return 'orange';
    if (strength < 80) return 'yellow';
    return 'green';
  },
};
